"use client";

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Eye,
  Download,
  Building2,
  Calendar,
  DollarSign,
  CheckCircle2,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, downloadFile } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Invoice {
  id: string;
  contract_id: string;
  client_id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  amount: string | number;
  currency: string;
  status: string;
  due_date: string;
  issued_date: string;
  paid_date: string | null;
  notes: string | null;
  created_at: string;
  client?: { id: string; company_name: string; contact_person: string };
  contract?: { id: string; service_type: string };
}

interface InvoicesResponse {
  success: boolean;
  message: string;
  data: Invoice[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const formatCurrency = (amount: number | string, currency: string = "USD") => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return `${currency} 0`;
  return `${currency} ${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} position="bottom" heightPercentage={80} showCloseButton={false}>
        <div className="h-full flex flex-col bg-black text-white">{children}</div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-50 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const InvoiceDrawer = ({
  invoice,
  isOpen,
  onClose,
  onMarkPaid,
  onDownload,
  isAdmin,
}: {
  invoice: Invoice | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkPaid: (id: string) => void;
  onDownload: (invoice: Invoice) => void;
  isAdmin: boolean;
}) => {
  if (!invoice) return null;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">{invoice.invoice_number}</h2>
          <p className="text-xs text-gray-400 mt-1">Invoice Details</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <div className={`inline-block px-3 py-1 text-xs font-semibold rounded-lg ${STATUS_COLORS[invoice.status] || "bg-gray-100 text-gray-800"}`}>
            {formatLabel(invoice.status)}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Client</p>
              <p className="text-xs text-gray-400">{invoice.client?.company_name || "—"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Amount</p>
              <p className="text-xs text-gray-400">{formatCurrency(invoice.amount, invoice.currency)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Billing Period</p>
              <p className="text-xs text-gray-400">
                {new Date(invoice.period_start).toLocaleDateString()} – {new Date(invoice.period_end).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Due Date</p>
              <p className="text-xs text-gray-400">{new Date(invoice.due_date).toLocaleDateString()}</p>
            </div>
          </div>

          {invoice.paid_date && (
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-xs font-semibold text-white">Paid On</p>
                <p className="text-xs text-gray-400">{new Date(invoice.paid_date).toLocaleDateString()}</p>
              </div>
            </div>
          )}

          {invoice.contract && (
            <div className="flex items-center gap-3">
              <Building2 className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Service</p>
                <p className="text-xs text-gray-400">{formatLabel(invoice.contract.service_type)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 border-t border-gray-800 space-y-2">
        <button
          onClick={() => onDownload(invoice)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
        {isAdmin && invoice.status !== "paid" && invoice.status !== "cancelled" && (
          <button
            onClick={() => onMarkPaid(invoice.id)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle2 className="w-4 h-4" />
            Mark as Paid
          </button>
        )}
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onView, onDownload }: { onView: () => void; onDownload: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDownload(); handleClose(); }}>
            <Download className="w-3 h-3" />
            Download PDF
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function InvoicesPage() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });

  useEffect(() => {
    const role = localStorage.getItem("userRole") || sessionStorage.getItem("userRole");
    setIsAdmin(role === "admin");
  }, []);

  const contractId = searchParams.get("contract_id");

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/invoices?page=${pagination.page}&page_size=${pagination.per_page}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (contractId) url += `&contract_id=${contractId}`;

      const response = await get<InvoicesResponse>(url);
      if (response.success) {
        setInvoices(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, statusFilter, contractId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await put(`/invoices/${id}`, { status: "paid" });
      fetchInvoices();
      setShowInvoiceDrawer(false);
      alert("Invoice marked as paid");
    } catch (error) {
      console.error("Error marking invoice paid:", error);
      alert("Error updating invoice");
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      await downloadFile(`/invoices/${invoice.id}/pdf`, `${invoice.invoice_number}.pdf`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Error downloading invoice");
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (invoice.client?.company_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDrawer(true);
  };

  const columns: ColumnDef<Invoice>[] = [
    {
      accessorKey: "invoice_number",
      header: "Invoice",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.invoice_number}</p>
          <p className="text-xs text-gray-600">{row.original.client?.company_name || "—"}</p>
        </div>
      ),
    },
    {
      id: "period",
      header: "Period",
      cell: ({ row }) => (
        <p className="text-xs text-gray-700">
          {new Date(row.original.period_start).toLocaleDateString()} – {new Date(row.original.period_end).toLocaleDateString()}
        </p>
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <p className="text-xs font-semibold text-gray-900">{formatCurrency(row.original.amount, row.original.currency)}</p>
      ),
    },
    {
      accessorKey: "due_date",
      header: "Due",
      cell: ({ row }) => (
        <p className="text-xs text-gray-600">{new Date(row.original.due_date).toLocaleDateString()}</p>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.original.status] || "bg-gray-100 text-gray-800"}`}>
          {formatLabel(row.original.status)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          onView={() => handleViewInvoice(row.original)}
          onDownload={() => handleDownload(row.original)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-3xl font-light text-gray-900 tracking-tight">Invoices</h1>
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary rounded-full"></div>
                <p className="text-sm font-light text-gray-500 tracking-wide">
                  Auto-generated recurring billing for client contracts
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 bg-black rounded-xl border border-gray-700 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by invoice number or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-sm bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-400 transition-colors"
              />
            </div>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="px-4 py-3 text-sm bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition-all duration-200 text-white font-light tracking-wide min-w-[100px]"
            >
              Filters
            </button>
          </div>

          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-light text-gray-300 mb-2 block tracking-wide">STATUS</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white font-light"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => setStatusFilter("all")}
                    className="w-full px-4 py-2 text-sm font-light text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition-all duration-200"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredInvoices}>
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredInvoices.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No invoices found</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    Invoices are generated automatically from active monthly/quarterly contracts.
                  </p>
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} invoices
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <InvoiceDrawer
        invoice={selectedInvoice}
        isOpen={showInvoiceDrawer}
        onClose={() => {
          setShowInvoiceDrawer(false);
          setSelectedInvoice(null);
        }}
        onMarkPaid={handleMarkPaid}
        onDownload={handleDownload}
        isAdmin={isAdmin}
      />
    </div>
  );
}

export default InvoicesPage;
